import { Fragment, useMemo } from "react";
import { ChevronLeft, ChevronRight, Calendar, LayoutGrid, List } from "lucide-react";
import {
  formatLocalMonthDay,
  formatLocalMonthDayYear,
  formatLocalMonthYear,
  formatLocalWeekday,
  formatLongWeekdayMonthDayYear,
  formatTime,
} from "@/lib/i18n";
import { cn } from "@/lib/utils";
import type { DragPreview, DragState } from "@/hooks/use-calendar-drag";
import { DAY_END_MINUTES, DAY_START_MINUTES, SLOT_MINUTES } from "@/hooks/use-calendar-drag";

export type CalendarView = "day" | "week" | "month";

export interface CalendarBooking {
  id: string;
  title: string;
  startTime: Date;
  endTime: Date;
  resourceId: string | null;
  serviceId?: string | null;
  assignedResourceIds?: string[];
  resourceName: string | null;
  contactName: string | null;
  status: "confirmed" | "pending" | "cancelled" | "completed" | "no_show";
  color?: string;
}

export interface CalendarResource {
  id: string;
  name: string;
  type: string;
  color: string;
}

interface CalendarGridProps {
  bookings: CalendarBooking[];
  resources: CalendarResource[];
  selectedDate: Date;
  view: CalendarView;
  onDateChange: (date: Date) => void;
  onViewChange: (view: CalendarView) => void;
  onBookingClick?: (bookingId: string) => void;
  onSlotClick?: (date: Date, minuteOfDay: number, resourceId?: string) => void;
  dragState?: DragState | null;
  dragPreview?: DragPreview | null;
  onSlotPointerDown?: (minuteOfDay: number, resourceId?: string) => void;
  onSlotPointerEnter?: (minuteOfDay: number, resourceId?: string) => void;
  onCalendarPointerUp?: () => void;
  onBookingMovePointerDown?: (bookingId: string, minuteOfDay: number, resourceId?: string) => void;
  onBookingResizePointerDown?: (bookingId: string, startMinute: number, endMinute: number, resourceId?: string) => void;
}

const SLOT_HEIGHT = 22;
const HOUR_LABEL_MINUTES = Array.from(
  { length: (DAY_END_MINUTES - DAY_START_MINUTES) / SLOT_MINUTES },
  (_, index) => DAY_START_MINUTES + (index * SLOT_MINUTES),
);

const STATUS_COLORS: Record<CalendarBooking["status"], string> = {
  confirmed: "var(--color-primary)",
  pending: "var(--color-warning)",
  cancelled: "var(--color-muted-foreground)",
  completed: "var(--color-success)",
  no_show: "var(--color-destructive)",
};

function minutesFromDate(date: Date): number {
  return (date.getHours() * 60) + date.getMinutes();
}

function formatMinuteLabel(minuteOfDay: number) {
  const hours = Math.floor(minuteOfDay / 60);
  const minutes = minuteOfDay % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
}

function getBookingBlockMetrics(booking: CalendarBooking) {
  const startMinute = Math.max(minutesFromDate(booking.startTime), DAY_START_MINUTES);
  const endMinute = Math.min(minutesFromDate(booking.endTime), DAY_END_MINUTES);
  const top = ((startMinute - DAY_START_MINUTES) / SLOT_MINUTES) * SLOT_HEIGHT;
  const height = Math.max(((endMinute - startMinute) / SLOT_MINUTES) * SLOT_HEIGHT, SLOT_HEIGHT);
  return {
    startMinute,
    endMinute,
    top,
    height,
  };
}

export function CalendarGrid({
  bookings,
  resources,
  selectedDate,
  view,
  onDateChange,
  onViewChange,
  onBookingClick,
  onSlotClick,
  dragState,
  dragPreview,
  onSlotPointerDown,
  onSlotPointerEnter,
  onCalendarPointerUp,
  onBookingMovePointerDown,
  onBookingResizePointerDown,
}: CalendarGridProps) {
  const navigateDate = (direction: -1 | 1) => {
    const nextDate = new Date(selectedDate);
    if (view === "day") nextDate.setDate(nextDate.getDate() + direction);
    else if (view === "week") nextDate.setDate(nextDate.getDate() + (direction * 7));
    else nextDate.setMonth(nextDate.getMonth() + direction);
    onDateChange(nextDate);
  };

  const dateLabel = useMemo(() => {
    if (view === "day") {
      return formatLongWeekdayMonthDayYear(selectedDate);
    }
    if (view === "week") {
      const start = new Date(selectedDate);
      start.setDate(start.getDate() - start.getDay());
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return `${formatLocalMonthDay(start)} — ${formatLocalMonthDayYear(end)}`;
    }
    return formatLocalMonthYear(selectedDate);
  }, [selectedDate, view]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigateDate(-1)} className="btn-press rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Previous">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button onClick={() => onDateChange(new Date())} className="rounded-md border border-border px-3 py-1 text-xs font-medium text-foreground hover:bg-muted">
            Today
          </button>
          <button onClick={() => navigateDate(1)} className="btn-press rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Next">
            <ChevronRight className="h-4 w-4" />
          </button>
          <h2 className="text-sm font-semibold text-foreground">{dateLabel}</h2>
        </div>
        <div className="flex rounded-md border border-border">
          {(["day", "week", "month"] as const).map((nextView) => (
            <button
              key={nextView}
              onClick={() => onViewChange(nextView)}
              className={cn(
                "flex items-center gap-1 px-3 py-1.5 text-xs font-medium transition-colors",
                view === nextView ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)]" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {nextView === "day" && <List className="h-3 w-3" />}
              {nextView === "week" && <LayoutGrid className="h-3 w-3" />}
              {nextView === "month" && <Calendar className="h-3 w-3" />}
              {nextView.charAt(0).toUpperCase() + nextView.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {view === "day" && (
          <DayView
            date={selectedDate}
            bookings={bookings}
            resources={resources}
            onBookingClick={onBookingClick}
            onSlotClick={onSlotClick}
            dragState={dragState}
            dragPreview={dragPreview}
            onSlotPointerDown={onSlotPointerDown}
            onSlotPointerEnter={onSlotPointerEnter}
            onCalendarPointerUp={onCalendarPointerUp}
            onBookingMovePointerDown={onBookingMovePointerDown}
            onBookingResizePointerDown={onBookingResizePointerDown}
          />
        )}
        {view === "week" && (
          <WeekView
            date={selectedDate}
            bookings={bookings}
            onBookingClick={onBookingClick}
          />
        )}
        {view === "month" && (
          <MonthView
            date={selectedDate}
            bookings={bookings}
            onDateChange={onDateChange}
            onViewChange={onViewChange}
          />
        )}
      </div>
    </div>
  );
}

function DayView({
  date,
  bookings,
  resources,
  onBookingClick,
  onSlotClick,
  dragState,
  dragPreview,
  onSlotPointerDown,
  onSlotPointerEnter,
  onCalendarPointerUp,
  onBookingMovePointerDown,
  onBookingResizePointerDown,
}: {
  date: Date;
  bookings: CalendarBooking[];
  resources: CalendarResource[];
  onBookingClick?: (id: string) => void;
  onSlotClick?: (date: Date, minuteOfDay: number, resourceId?: string) => void;
  dragState?: DragState | null;
  dragPreview?: DragPreview | null;
  onSlotPointerDown?: (minuteOfDay: number, resourceId?: string) => void;
  onSlotPointerEnter?: (minuteOfDay: number, resourceId?: string) => void;
  onCalendarPointerUp?: () => void;
  onBookingMovePointerDown?: (bookingId: string, minuteOfDay: number, resourceId?: string) => void;
  onBookingResizePointerDown?: (bookingId: string, startMinute: number, endMinute: number, resourceId?: string) => void;
}) {
  const dayStart = new Date(date);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(date);
  dayEnd.setHours(23, 59, 59, 999);

  const columnResources = resources.length > 0 ? resources : [{ id: "__all", name: "Unassigned", type: "person", color: "var(--color-primary)" }];
  const showSwimlanes = resources.length > 1;
  const dayBookings = bookings.filter((booking) => booking.startTime >= dayStart && booking.startTime <= dayEnd);

  return (
    <div
      className="grid bg-[var(--color-surface)]"
      style={{ gridTemplateColumns: `72px repeat(${columnResources.length}, minmax(220px, 1fr))` }}
      onPointerUp={() => onCalendarPointerUp?.()}
    >
      <div className="sticky top-0 z-10 border-b border-r border-border bg-[var(--color-surface)] p-2" />
      {columnResources.map((resource) => (
        <div key={resource.id} className="sticky top-0 z-10 border-b border-border bg-[var(--color-surface)] p-2 text-center">
          <span className="text-xs font-medium text-foreground">{showSwimlanes ? resource.name : "Schedule"}</span>
        </div>
      ))}

      {HOUR_LABEL_MINUTES.map((minuteOfDay, index) => (
        <Fragment key={minuteOfDay}>
          <div className="border-b border-r border-border px-2 py-1 text-right">
            <span className={cn(
              "text-[10px] text-muted-foreground",
              minuteOfDay % 60 === 0 ? "opacity-100" : "opacity-0",
            )}>
              {formatMinuteLabel(minuteOfDay)}
            </span>
          </div>
          {columnResources.map((resource) => (
            <div
              key={`${resource.id}-${minuteOfDay}`}
              data-testid={`calendar-slot-${resource.id}-${minuteOfDay}`}
              className={cn(
                "border-b border-border transition-colors",
                minuteOfDay % 60 === 0 ? "border-t border-t-border/60" : "",
                dragState?.active ? "bg-[var(--color-primary-light)]/10" : "hover:bg-[var(--color-primary-light)]/25",
              )}
              style={{ height: SLOT_HEIGHT }}
              onClick={() => {
                if (!onSlotPointerDown) {
                  onSlotClick?.(date, minuteOfDay, resource.id === "__all" ? undefined : resource.id);
                }
              }}
              onPointerDown={() => onSlotPointerDown?.(minuteOfDay, resource.id === "__all" ? undefined : resource.id)}
              onPointerEnter={() => onSlotPointerEnter?.(minuteOfDay, resource.id === "__all" ? undefined : resource.id)}
            />
          ))}
        </Fragment>
      ))}

      {columnResources.map((resource, columnIndex) => {
        const resourceBookings = dayBookings.filter((booking) => (
          resource.id === "__all" ? true : booking.resourceId === resource.id
        ));

        return (
          <div
            key={`${resource.id}-overlay`}
            className="pointer-events-none relative"
            style={{
              gridColumn: columnIndex + 2,
              gridRow: `2 / span ${HOUR_LABEL_MINUTES.length}`,
            }}
          >
            {dragPreview && (dragPreview.resourceId ?? "__all") === resource.id ? (
              <div
                className="absolute left-1 right-1 rounded-lg border border-dashed border-[var(--color-primary)] bg-[var(--color-primary)]/12"
                style={{
                  top: ((dragPreview.startMinute - DAY_START_MINUTES) / SLOT_MINUTES) * SLOT_HEIGHT,
                  height: Math.max(((dragPreview.endMinute - dragPreview.startMinute) / SLOT_MINUTES) * SLOT_HEIGHT, SLOT_HEIGHT),
                }}
              />
            ) : null}

            {resourceBookings.map((booking) => {
              const metrics = getBookingBlockMetrics(booking);
              return (
                <div
                  key={booking.id}
                  className="pointer-events-auto absolute left-1 right-1"
                  style={{
                    top: metrics.top,
                    height: metrics.height,
                  }}
                >
                  <BookingCard
                    booking={booking}
                    height={metrics.height}
                    onClick={() => onBookingClick?.(booking.id)}
                    onMovePointerDown={() => onBookingMovePointerDown?.(booking.id, metrics.startMinute, booking.resourceId ?? undefined)}
                    onResizePointerDown={() => onBookingResizePointerDown?.(booking.id, metrics.startMinute, metrics.endMinute, booking.resourceId ?? undefined)}
                  />
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function WeekView({ date, bookings, onBookingClick }: {
  date: Date;
  bookings: CalendarBooking[];
  onBookingClick?: (id: string) => void;
}) {
  const weekStart = new Date(date);
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  const days = Array.from({ length: 7 }, (_, index) => {
    const next = new Date(weekStart);
    next.setDate(next.getDate() + index);
    return next;
  });

  return (
    <div className="grid grid-cols-[72px_repeat(7,1fr)]">
      <div className="sticky top-0 z-10 border-b border-r border-border bg-[var(--color-surface)]" />
      {days.map((day) => (
        <div key={day.toISOString()} className="sticky top-0 z-10 border-b border-border bg-[var(--color-surface)] p-2 text-center">
          <span className="text-[10px] text-muted-foreground">{formatLocalWeekday(day)}</span>
          <span className={cn(
            "block text-sm font-medium",
            day.toDateString() === new Date().toDateString() ? "text-[var(--color-primary)]" : "text-foreground",
          )}>
            {day.getDate()}
          </span>
        </div>
      ))}

      {Array.from({ length: 12 }, (_, index) => DAY_START_MINUTES + (index * 60)).map((minuteOfDay) => (
        <Fragment key={minuteOfDay}>
          <div className="border-b border-r border-border px-2 py-3 text-right text-[10px] text-muted-foreground">
            {formatMinuteLabel(minuteOfDay)}
          </div>
          {days.map((day) => {
            const slotBookings = bookings.filter((booking) => (
              booking.startTime.toDateString() === day.toDateString()
              && booking.startTime.getHours() === Math.floor(minuteOfDay / 60)
            ));

            return (
              <div key={`${day.toISOString()}-${minuteOfDay}`} className="min-h-[52px] border-b border-border px-1 py-1 hover:bg-[var(--color-primary-light)]/20">
                {slotBookings.map((booking) => (
                  <BookingCard
                    key={booking.id}
                    booking={booking}
                    compact
                    height={40}
                    onClick={() => onBookingClick?.(booking.id)}
                  />
                ))}
              </div>
            );
          })}
        </Fragment>
      ))}
    </div>
  );
}

function MonthView({ date, bookings, onDateChange, onViewChange }: {
  date: Date;
  bookings: CalendarBooking[];
  onDateChange: (date: Date) => void;
  onViewChange: (view: CalendarView) => void;
}) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = Array.from({ length: 42 }, (_, index) => {
    const dayNumber = index - firstDay + 1;
    if (dayNumber < 1 || dayNumber > daysInMonth) {
      return null;
    }
    return new Date(year, month, dayNumber);
  });

  return (
    <div className="grid grid-cols-7">
      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((label) => (
        <div key={label} className="border-b border-border p-2 text-center text-xs font-medium text-muted-foreground">
          {label}
        </div>
      ))}

      {cells.map((day, index) => {
        if (!day) {
          return <div key={`empty-${index}`} className="min-h-[96px] border-b border-r border-border bg-[var(--color-muted)]/20" />;
        }

        const dayBookings = bookings.filter((booking) => booking.startTime.toDateString() === day.toDateString());
        const isToday = day.toDateString() === new Date().toDateString();

        return (
          <button
            key={day.toISOString()}
            type="button"
            className="min-h-[96px] border-b border-r border-border p-2 text-left transition-colors hover:bg-[var(--color-primary-light)]/20"
            onClick={() => {
              onDateChange(day);
              onViewChange("day");
            }}
          >
            <span className={cn(
              "inline-flex h-6 w-6 items-center justify-center rounded-full text-xs",
              isToday ? "bg-[var(--color-primary)] font-bold text-white" : "text-foreground",
            )}>
              {day.getDate()}
            </span>
            {dayBookings.length > 0 ? (
              <div className="mt-1.5 space-y-1">
                {dayBookings.slice(0, 3).map((booking) => (
                  <div
                    key={booking.id}
                    className="truncate rounded px-1.5 py-0.5 text-[10px] font-medium text-white"
                    style={{ backgroundColor: STATUS_COLORS[booking.status] }}
                  >
                    {booking.title}
                  </div>
                ))}
                {dayBookings.length > 3 ? (
                  <span className="text-[10px] text-muted-foreground">+{dayBookings.length - 3} more</span>
                ) : null}
              </div>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function BookingCard({
  booking,
  compact = false,
  height,
  onClick,
  onMovePointerDown,
  onResizePointerDown,
}: {
  booking: CalendarBooking;
  compact?: boolean;
  height: number;
  onClick?: () => void;
  onMovePointerDown?: () => void;
  onResizePointerDown?: () => void;
}) {
  return (
    <div
      className={cn(
        "group relative h-full w-full cursor-pointer overflow-hidden rounded-lg border border-border/60 bg-[var(--color-card)] px-2 py-1 shadow-sm",
        compact ? "min-h-[40px]" : "",
      )}
      style={{
        borderLeftWidth: 4,
        borderLeftColor: STATUS_COLORS[booking.status],
        minHeight: compact ? 40 : height,
      }}
      onClick={(event) => {
        event.stopPropagation();
        onClick?.();
      }}
      onPointerDown={(event) => {
        event.stopPropagation();
        onMovePointerDown?.();
      }}
    >
      <p className="truncate text-xs font-semibold text-foreground">{booking.title}</p>
      {!compact ? (
        <>
          <p className="truncate text-[11px] text-muted-foreground">{booking.contactName ?? "Unassigned contact"}</p>
          <p className="truncate text-[10px] text-muted-foreground">
            {formatTime(booking.startTime)} - {formatTime(booking.endTime)}
          </p>
        </>
      ) : null}
      {!compact ? (
        <button
          type="button"
          aria-label={`Resize ${booking.title}`}
          className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize rounded-b-lg bg-[var(--color-foreground)]/10 opacity-0 transition-opacity group-hover:opacity-100"
          onPointerDown={(event) => {
            event.stopPropagation();
            onResizePointerDown?.();
          }}
        />
      ) : null}
    </div>
  );
}
